import { useState, useEffect, useCallback } from 'react';
import useSlingApi from '@sdk/api/useSlingApi';

import { getToastIntegrationStatus } from '@web/components/npsScore/npsScore.hooks';
import {
  sendSurveyFeedback,
  shouldCheckForSubscriptionPreferenceSurvey,
  updateSurveyDisplayed,
} from '@web/components/subscriptionPreferenceSurvey/utils';
import { Props as SubscriptionPreferenceSurveyProps } from '@web/components/subscriptionPreferenceSurvey/SubscriptionPreferenceSurvey';

type Props = SubscriptionPreferenceSurveyProps & {
  comment: string;
  reason: string;
  other: string;
};

const DISPLAY_DELAY = 3 * 1000;

export const useSubscriptionPreferenceSurvey = ({
  user,
  wasFreemiumSelected,
  reason,
  other,
  comment,
  saveClientFlags,
  totalUserCount,
  joinedUserCount,
  locationsCount,
  onClose,
  forceDisplay,
}: Props) => {
  const { isToastBilling } = user.org;
  const [isVisible, setIsVisible] = useState(false);
  const [wasDisplayed, setWasDisplayed] = useState(false);
  const api = useSlingApi();

  const shouldDisplaySurvey = shouldCheckForSubscriptionPreferenceSurvey(user, wasFreemiumSelected);
  const submitSurvey = useCallback(async () => {
    const { data: integrations } = await api.integrations.getAvailableIntegrations();
    const toastIntegrationStatus = getToastIntegrationStatus(integrations, user);

    sendSurveyFeedback(
      { reason, other, comment },
      { user, saveClientFlags, totalUserCount, joinedUserCount, locationsCount },
      isToastBilling,
      toastIntegrationStatus
    );
  }, [
    reason,
    other,
    comment,
    user,
    saveClientFlags,
    totalUserCount,
    joinedUserCount,
    locationsCount,
    isToastBilling,
    api.integrations,
  ]);

  const closeModal = () => {
    updateSurveyDisplayed(saveClientFlags);
    setIsVisible(false);
    setWasDisplayed(true);
    onClose?.();
  };

  useEffect(() => {
    if ((shouldDisplaySurvey && !wasDisplayed) || forceDisplay) {
      const timeoutId = setTimeout(() => {
        setIsVisible(true);
        setWasDisplayed(true);
      }, DISPLAY_DELAY);
      return (): void => clearTimeout(timeoutId);
    }
  }, [user, shouldDisplaySurvey, wasDisplayed, forceDisplay]);

  return {
    isVisible,
    closeModal,
    submitSurvey,
    isToastBilling,
  };
};
